import { confirmEmptyFolderSubText } from '../strings.locstring.json';
import {
    allUnreadItems,
    allFlaggedItems,
    allToMeItems,
    allItems,
    confirmPermanentEmptyFolderSubText,
    confirmEmptyFolderForFolderUnderDeletedItemsSubText,
    confirmEmptyFolderText,
} from './emptyFolder.locstring.json';
import loc, { format } from 'owa-localize';
import * as undoActions from 'owa-mail-undo';
import DisposalType from 'owa-service/lib/contract/DisposalType';
import FocusedViewFilter from 'owa-service/lib/contract/FocusedViewFilter';
import folderIdToName from 'owa-session-store/lib/utils/folderIdToName';
import getDeleteAllConfirmText from './helpers/getDeleteAllConfirmText';
import getSelectedFolder from 'owa-mail-store/lib/utils/getSelectedFolder';
import { isUnderDeletedItemsFolderFromAnyMailbox } from 'owa-mail-store';

import { action } from 'satcheljs/lib/legacy';
import { ActionSource } from 'owa-mail-store';
import { confirm, ConfirmCustomizationOptions, DialogResponse } from 'owa-confirm-dialog';
import ViewFilter from 'owa-service/lib/contract/ViewFilter';
import folderStore, { Folder, getEffectiveFolderDisplayName } from 'owa-folders';

import { getListViewTypeForFolder, listViewStore, TableQueryType } from 'owa-mail-list-store';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import { lazyOnBulkActionSubmitted } from 'owa-bulk-action-store';
import { logUsage } from 'owa-analytics';
import getEmptyFolderHelperProperties from './helpers/emptyFolderHelper';
import emptyFolderWithoutConfirmation from './actionCreators/emptyFolderWithoutConfirmation';
import InboxClassificationFilter from 'owa-mail-inbox-plus-store/lib/store/schema/InboxClassificationFilter';
import getFolderScopeText from 'owa-mail-list-store/lib/selectors/getFolderScopeText';

/**
 * Get the type of items to delete based on view filter
 * @param viewFilter the view filter
 * @returns the text which indicates type of items to delete based on the view filter
 */
function getItemsTypeText(viewFilter: ViewFilter): string {
    switch (viewFilter) {
        case 'Unread':
            return loc(allUnreadItems);

        case 'Flagged':
            return loc(allFlaggedItems);

        case 'ToOrCcMe':
            return loc(allToMeItems);

        case 'All':
        default:
            return loc(allItems);
    }
}

const emptyFolderActionName = 'emptyFolderAction';
export { emptyFolderActionName };

/**
 * Delete all conversations or items in the specified folder
 * @param folder the folder to be emptied
 * @param focusedViewFilter the focused view filter
 * @param inboxClassificationFilter the inbox classification filter
 * @param viewFilter the view filter
 * @param isExplicitSoftDelete flag to indicate whether disposal type is soft delete
 * @param clientLastSyncTime client last sync time used as a watermark
 * @param rowKeysToExclude row keyss to exclude from empty folder operation
 */
export default action(emptyFolderActionName)(function emptyFolder(
    folder: Folder,
    focusedViewFilter: FocusedViewFilter,
    inboxClassificationFilter: InboxClassificationFilter,
    viewFilter: ViewFilter,
    isExplicitSoftDelete: boolean,
    actionSource: ActionSource,
    rowKeysToExclude: string[],
    clientLastSyncTime: Date,
    tableViewId?: string
): Promise<void> {
    let confirmEmptyFolderSubtext;
    let disposalType: DisposalType;
    let deleteSubFolders;

    undoActions.clearLastUndoableAction();

    const folderScopeText = getFolderScopeText(
        focusedViewFilter,
        inboxClassificationFilter,
        TableQueryType.Folder,
        getEffectiveFolderDisplayName(folder)
    );
    const itemsTypeText = getItemsTypeText(viewFilter);
    const exclusionListCount = rowKeysToExclude ? rowKeysToExclude.length : 0;
    let confirmEmptyFolderSubTextString;
    let folderToBeEmptied = folder;

    // If a tableViewId is specified we are emptying the table, in which case we want get the actual folder
    // to get the emptyFolder helper properties
    if (tableViewId) {
        const tableView = listViewStore.tableViews.get(tableViewId);
        folderToBeEmptied = folderStore.folderTable.get(tableView.tableQuery.folderId);
    }

    const {
        isFolderDeletedItemsFolder,
        isFolderDumpsterFolder,
        deletedItemsFolderId,
        shouldSupportBulkActions,
        treeType,
    } = getEmptyFolderHelperProperties(folderToBeEmptied);

    if (isFolderDumpsterFolder) {
        confirmEmptyFolderSubTextString = loc(confirmPermanentEmptyFolderSubText);
        deleteSubFolders = false;
        disposalType = 'HardDelete';
    } else if (isFolderDeletedItemsFolder || folder.DistinguishedFolderId == 'junkemail') {
        confirmEmptyFolderSubTextString = loc(confirmPermanentEmptyFolderSubText);
        deleteSubFolders = !isConsumer();
        // This is to ensure the contract on server that clientLastSyncTime and deleteSubFolders cannot be present at the same time
        clientLastSyncTime = deleteSubFolders ? null : clientLastSyncTime;
        disposalType = 'SoftDelete';
    } else if (isUnderDeletedItemsFolderFromAnyMailbox(folder)) {
        confirmEmptyFolderSubTextString = loc(confirmEmptyFolderForFolderUnderDeletedItemsSubText);
        deleteSubFolders = false;
        disposalType = 'SoftDelete';
    } else {
        if (isExplicitSoftDelete) {
            confirmEmptyFolderSubTextString = loc(confirmPermanentEmptyFolderSubText);
            disposalType = 'SoftDelete';
        } else {
            confirmEmptyFolderSubTextString = loc(confirmEmptyFolderSubText);
            disposalType = 'MoveToDeletedItems';
        }

        deleteSubFolders = false;
    }

    confirmEmptyFolderSubtext = format(
        confirmEmptyFolderSubTextString,
        itemsTypeText,
        folderScopeText
    );

    const folderId = folder.FolderId.Id;
    const numberOfRowsToDelete = folder.TotalCount - exclusionListCount;

    return confirm(
        getDeleteAllConfirmText(
            actionSource,
            getListViewTypeForFolder(folderId) /* listViewType */,
            numberOfRowsToDelete
        ),
        confirmEmptyFolderSubtext,
        false /* resolveImmediately */,
        <ConfirmCustomizationOptions>{
            // Wait until dialog is dismissed to avoid focus interruption
            delayCallbackAfterAnimation: true,
            okText: loc(confirmEmptyFolderText),
        }
    ).then(async (response: DialogResponse) => {
        if (response === DialogResponse.ok) {
            if (shouldSupportBulkActions) {
                lazyOnBulkActionSubmitted.importAndExecute(
                    folderId,
                    'EmptyFolder' /* scenario */,
                    null /* customData */,
                    numberOfRowsToDelete /* totalCount */
                );
            }

            // TODO : Add check for shared folders during implementation
            logUsage('TnS_EmptyFolder', [
                actionSource,
                numberOfRowsToDelete,
                folderIdToName(folderId),
                folderIdToName(getSelectedFolder().FolderId.Id),
                disposalType,
                treeType,
                folder.TotalCount,
                exclusionListCount,
            ]);

            emptyFolderWithoutConfirmation(
                folder,
                disposalType,
                deleteSubFolders,
                clientLastSyncTime,
                rowKeysToExclude,
                tableViewId,
                deletedItemsFolderId,
                shouldSupportBulkActions
            );
        }
    });
});
